/** Stable TanStack Query keys for cache invalidation. */
export const queryKeys = {
  core: {
    streamConfig: ['core', 'stream-config'],
    featuredContent: ['core', 'featured-content'],
    maintenanceStatus: ['core', 'maintenance-status'],
  },
  search: {
    suggestions: (q) => ['search', 'suggestions', q ?? ''],
  },
  shop: {
    userPurchases: ['shop', 'user', 'purchases'],
    genres: ['shop', 'genres'],
    albumsFeatured: ['shop', 'albums', 'featured'],
    albumsPopular: ['shop', 'albums', 'popular'],
    albumsList: ['shop', 'albums', 'list'],
    tracksList: ['shop', 'tracks', 'list'],
    checkout: (cartSignature, currency) => ['shop', 'checkout', cartSignature, currency],
  },
  playlists: {
    my: ['playlists', 'my'],
    detail: (playlistId) => ['playlists', 'detail', playlistId],
    tracks: (playlistId) => ['playlists', 'tracks', playlistId],
  },
  albums: {
    detail: (route) => ['albums', 'detail', route],
  },
  tracks: {
    detail: (trackKey) => ['tracks', 'detail', trackKey ?? null],
    artistPublic: (username, excludeUuid) => [
      'tracks',
      'artist-public',
      username ?? null,
      excludeUuid ?? null,
    ],
    similar: (genre, excludeUuid) => ['tracks', 'similar', genre ?? null, excludeUuid ?? null],
  },
  storage: {
    plans: (currency) => ['storage', 'plans', currency],
    me: ['storage', 'me'],
  },
  artist: {
    publicProfile: (routeParam) => ['artist', 'public', routeParam],
    albums: (username) => ['artist', 'albums', username ?? null],
    availableTracks: (effectiveArtist) => [
      'artist',
      'available-tracks',
      effectiveArtist ?? 'self',
    ],
    dashboardTracks: (scope) => ['artist', 'dashboard-tracks', scope ?? 'self'],
    dashboardAlbums: (scope) => ['artist', 'dashboard-albums', scope ?? 'self'],
    trackEdit: (trackUuid) => ['artist', 'track-edit', trackUuid ?? null],
    verification: ['artist', 'verification'],
  },
  dashboard: {
    overview: (role, userId, period = '30d') => [
      'dashboard',
      'overview',
      role ?? 'guest',
      userId ?? null,
      period,
    ],
    myOrders: ['dashboard', 'my-orders'],
    artistSales: (period = 'all') => ['dashboard', 'artist-sales', period],
    artistAnalytics: (limit, showCountry, period = '30d') => [
      'dashboard',
      'artist-analytics',
      limit,
      showCountry ? 'country' : 'basic',
      period,
    ],
  },
  content: {
    verifiedArtists: ['content', 'verified-artists'],
    stripeAccount: ['content', 'stripe-account'],
  },
  accounts: {
    profile: ['accounts', 'profile'],
    privacySettings: ['accounts', 'privacy-settings'],
    verificationStatus: ['accounts', 'verification-status'],
    roleChangeStatus: ['accounts', 'role-change-status'],
    twoFactorAuth: ['accounts', 'two-factor-auth'],
    usernameCheck: (username) => ['accounts', 'username-check', username ?? ''],
  },
  donations: {
    subscription: (token) => ['donations', 'subscription', token ?? null],
  },
  user: {
    publicProfile: (username, isOwnProfile = false) => [
      'user',
      'public-profile',
      username,
      isOwnProfile ? 'own' : 'other',
    ],
  },
  admin: {
    dashboard: ['admin', 'dashboard'],
    analytics: ['admin', 'analytics'],
    reports: ['admin', 'reports'],
    tracks: ['admin', 'tracks'],
    orders: ['admin', 'orders'],
    systemSettings: ['admin', 'system-settings'],
    managerDashboard: ['admin', 'manager-dashboard'],
  },
  payment: {
    shopStripeConfig: ['payment', 'stripe-config', 'shop'],
    donationStripeConfig: ['payment', 'stripe-config', 'donation'],
  },
  home: {
    featuredTracks: ['home', 'featured-tracks'],
    popularTracks: ['home', 'popular-tracks'],
    hotTracks: ['home', 'hot-tracks'],
    coldTracks: ['home', 'cold-tracks'],
    hotAlbums: ['home', 'hot-albums'],
    coldAlbums: ['home', 'cold-albums'],
    albumsLatest: ['home', 'albums', 'latest'],
  },
};

/** Stable key for album detail routes (`id` or `username`+`slug`). */
export function albumDetailQueryKey({ id, username, slug }) {
  if (username && slug) {
    return queryKeys.albums.detail({ username, slug });
  }
  return queryKeys.albums.detail({ id: id ?? null });
}
